import {ActionList} from '@primer/react'
import {graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {LABELS} from '../../constants'
import type {RelationsSectionFragment$key} from './__generated__/RelationsSectionFragment.graphql'
import type {RelationsSectionQuery} from './__generated__/RelationsSectionQuery.graphql'
import {ReadonlySectionHeader} from './ReadonlySectionHeader'
import {Section} from './Section'
import {TrackedBy} from './TrackedByItems'

export const RelationsSectionGraphqlQuery = graphql`
  query RelationsSectionQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issue(number: $number) {
        ...RelationsSectionFragment
      }
    }
  }
`

const RelationsSectionFragment = graphql`
  fragment RelationsSectionFragment on Issue {
    id
    trackedInIssues(first: 10) {
      totalCount
    }
    ...TrackedByItems
  }
`

type RelationsSectionProps = {
  queryRef: PreloadedQuery<RelationsSectionQuery>
  onLinkClick?: (event: MouseEvent) => void
}

type RelationsSectionInternalProps = Omit<RelationsSectionProps, 'queryRef'> & {
  issue: RelationsSectionFragment$key
}

export function RelationsSection({queryRef, ...rest}: RelationsSectionProps) {
  const preloadedData = usePreloadedQuery<RelationsSectionQuery>(RelationsSectionGraphqlQuery, queryRef)
  return preloadedData.repository && preloadedData.repository.issue ? (
    <RelationsSectionInternal issue={preloadedData.repository.issue} {...rest} />
  ) : null
}

export function RelationsSectionInternal({issue, onLinkClick}: RelationsSectionInternalProps) {
  const data = useFragment(RelationsSectionFragment, issue)

  const {trackedInIssues} = data
  const hasRelations = trackedInIssues.totalCount > 0

  return (
    <Section
      sectionHeader={<ReadonlySectionHeader title={LABELS.sectionTitles.relations} />}
      emptyText={hasRelations ? undefined : LABELS.emptySections.relations}
    >
      <ActionList sx={{py: 0, px: 0, width: '100%'}}>
        <TrackedBy issue={data} onLinkClick={onLinkClick} />
      </ActionList>
    </Section>
  )
}

try{ RelationsSection.displayName ||= 'RelationsSection' } catch {}
try{ RelationsSectionInternal.displayName ||= 'RelationsSectionInternal' } catch {}